import { renderToString } from 'react-dom/server';
import { Route, SEOConfig } from '../../types';
import React from 'react';

export interface CSRContext {
    route: Route;
    params: { [key: string]: string };
    searchParams: { [key: string]: string | string[] | undefined };
    seoConfig?: SEOConfig;
}

export function prerenderPage(
    _route: Route,
    App: React.ComponentType<any>,
    context?: CSRContext
): string {
    const props = {
        params: context?.params || {},
        searchParams: context?.searchParams || {}
    };

    const content = renderToString(React.createElement(App, props));

    return generateHTMLTemplate(content, context?.seoConfig, ['/assets/index.js']);
}

function generateHTMLTemplate(
    content: string,
    seoConfig?: SEOConfig,
    scripts?: string[]
): string {
    const metaTags = seoConfig ? generateMetaTags(seoConfig) : '';
    const scriptTags = scripts ? scripts.map(src => `<script src="${src}"></script>`).join('\n') : '';

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${metaTags}
  <script type="module" src="/@vite/client"></script>
</head>
<body>
  <div id="root">${content}</div>
  ${scriptTags}
</body>
</html>`;
}

function generateMetaTags(config: SEOConfig): string {
    const tags: string[] = [];

    if (config.title) {
        tags.push(`<title>${escapeHtml(config.title)}</title>`);
    }

    if (config.description) {
        tags.push(`<meta name="description" content="${escapeHtml(config.description)}">`);
    }

    if (config.ogTitle || config.title) {
        tags.push(`<meta property="og:title" content="${escapeHtml(config.ogTitle || config.title!)}">`);
    }

    if (config.ogDescription || config.description) {
        tags.push(`<meta property="og:description" content="${escapeHtml(config.ogDescription || config.description!)}">`);
    }

    if (config.ogImage) {
        tags.push(`<meta property="og:image" content="${escapeHtml(config.ogImage)}">`);
    }

    const twitterCard = config.twitterCard || 'summary_large_image';
    tags.push(`<meta name="twitter:card" content="${escapeHtml(twitterCard)}">`);

    if (config.ogTitle || config.title) {
        tags.push(`<meta name="twitter:title" content="${escapeHtml(config.ogTitle || config.title!)}">`);
    }

    if (config.ogDescription || config.description) {
        tags.push(`<meta name="twitter:description" content="${escapeHtml(config.ogDescription || config.description!)}">`);
    }

    if (config.ogImage) {
        tags.push(`<meta name="twitter:image" content="${escapeHtml(config.ogImage)}">`);
    }

    if (config.keywords && config.keywords.length > 0) {
        tags.push(`<meta name="keywords" content="${escapeHtml(config.keywords.join(', '))}">`);
    }

    if (config.canonical) {
        tags.push(`<link rel="canonical" href="${escapeHtml(config.canonical)}">`);
    }

    return tags.join('\n');
}

function escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, (m) => map[m]);
}
